import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { FC, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { inFlightRunStatusText, useRunEmailExecBrief } from '../../../../../../hooks/useRunEmailExecBrief';
import { useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { useEmailExecBriefsQuery } from '../../../../../../queries/EmailExecAssistant/useEmailExecBriefsQuery';
import { useLatestEmailExecDigestSuspenseQuery } from '../../../../../../queries/EmailExecAssistant/useLatestEmailExecDigestQuery';
import {
	selectBoundEmailExecTemplate,
	useEmailExecAssistantTemplatesQuery,
	useEmailExecAssistantTemplatesSuspenseQuery,
} from '../../../../../../queries/EmailExecAssistant/useEmailExecTemplates';
import { ROUTES } from '../../../../../constants/routes';
import { styleSheet } from '../../styles';
import { DashboardInboxAssistantFirstRunRow } from './DashboardInboxAssistantFirstRunRow';
import { DashboardInboxAssistantLiveRow } from './DashboardInboxAssistantLiveRow';

export const INBOX_ASSISTANT_TELEMETRY_CATEGORY = 'DashboardInboxAssistantRow';

export const useInboxAssistantRowVisibility = (): { visible: boolean } => {
	const userSession = useUserSession();
	const enabled = !!userSession.account?.features?.emailExec?.enabled;
	const templatesQuery = useEmailExecAssistantTemplatesQuery({ enabled });
	if (!enabled || templatesQuery.isError) {
		return { visible: false };
	}
	return { visible: templatesQuery.isPending || !!selectBoundEmailExecTemplate(templatesQuery.data ?? []) };
};

export const DashboardInboxAssistantRow: FC = () => {
	const { logEvent } = useEventLogging(INBOX_ASSISTANT_TELEMETRY_CATEGORY);
	const templatesQuery = useEmailExecAssistantTemplatesSuspenseQuery({
		meta: { errorLogging: { category: INBOX_ASSISTANT_TELEMETRY_CATEGORY, action: 'LoadTemplates-Error' } },
	});
	const latestDigestQuery = useLatestEmailExecDigestSuspenseQuery({
		meta: { errorLogging: { category: INBOX_ASSISTANT_TELEMETRY_CATEGORY, action: 'LoadLatestDigest-Error' } },
	});
	const briefsQuery = useEmailExecBriefsQuery();
	const { runBrief, isRunning, inFlightRun } = useRunEmailExecBrief({ source: 'dashboardCard' });

	const template = selectBoundEmailExecTemplate(templatesQuery.data ?? []);
	const digest = latestDigestQuery.data;

	if (!template) {
		return null;
	}

	const latestRun: Api.IEmailExecBriefSystemJob | undefined = briefsQuery.data?.[0];
	const note: ReactNode = isRunning && inFlightRun ? inFlightRunStatusText(inFlightRun) : null;
	const failureNote: ReactNode =
		latestRun?.status === 'Failed' ? (
			<NavLink
				className={css(styleSheet.failureNote)}
				to={ROUTES.emailExec.briefs}
				onClick={() => logEvent('ViewHistoryClicked', { from: 'failureNote' })}
			>
				Your latest run didn’t finish — view history
			</NavLink>
		) : null;

	if (!digest) {
		return (
			<DashboardInboxAssistantFirstRunRow
				detail={note ?? failureNote}
				isRunning={isRunning}
				onRunNow={() => runBrief(template.id)}
			/>
		);
	}

	return <DashboardInboxAssistantLiveRow digest={digest} detail={note ?? failureNote} />;
};
